<template>
  <div class="flex w-full items-center justify-center space-x-3 px-4 pb-4">
    <div class="w-1/6">
      {{ type === "input" ? $t("COMMON.INPUT") : $t("COMMON.OUTPUT") }}:
    </div>
    <div class="flex min-w-0 flex-1 flex-col overflow-hidden">
      <tooltip
        ref="templatesTooltip"
        class="block max-w-full overflow-hidden"
        placement="bottom-start"
        trigger="click"
        hide-on-click="toggle"
        :on-click-outside="hideDropdown"
        append-to="parent"
        :arrow="false"
        sticky="reference"
        theme="none"
        interactive
        :popper-options="popperOptions"
      >
        <template #default>
          <lf-pill
            size="sm"
            class="template-pill group h-[22px] max-w-full cursor-pointer overflow-hidden"
            color-index="35"
            opaque
          >
            <div class="flex min-w-0 items-center gap-x-2">
              <span
                data-cy="template-name"
                class="truncate text-left text-xs font-normal"
              >
                {{ selectedInputDevice?.label || $t("COMMON.DEFAULT") }}
              </span>
              <icon-base
                :icon="IconArrowUp"
                height="10"
                width="10"
                class="text-flush-orange group-hover:visible"
                :class="
                  !templatesTooltip?.state.isVisible
                    ? 'invisible -translate-y-1 rotate-180'
                    : 'invisible'
                "
              />
            </div>
          </lf-pill>
        </template>
        <template #content>
          <ul
            class="shadow-lggrid h-auto max-h-100 max-w-80 grid-cols-1 gap-y-2 overflow-x-hidden overflow-y-auto rounded-md bg-white p-2 px-1 text-sm text-headline"
          >
            <li
              v-for="item in devices"
              :key="item.deviceId"
              v-tooltipable
              class="cursor-pointer truncate rounded p-2 text-xs whitespace-nowrap hover:bg-grey-hover"
              :class="{
                'bg-primary-background-active':
                  selectedDevice === item.deviceId,
              }"
              @click="setDevice(item)"
            >
              {{ item.label }}
            </li>
          </ul>
        </template>
      </tooltip>
    </div>
    <span
      :title="$t('TWILIO.REFRESH_DEVICES')"
      class="cursor-pointer"
      @click="emit('device:refresh')"
    >
      <icon-base :icon="IconReload" view-box="4 4 16 16" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from "vue";

import IconArrowUp from "@/components/icons/IconArrowUp.vue";
import IconReload from "@/components/icons/IconReload.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { TippyComponent } from "vue-tippy";

const { type } = defineProps<{
  devices: MediaDeviceInfo[] | null;
  selectedDevice?: string;
  type: "input" | "output";
}>();

const emit = defineEmits<{
  "device:select": [id: string, type: "input" | "output"];
  "device:refresh": [];
}>();

const selectedInputDevice = ref<MediaDeviceInfo | null>(null);
const templatesTooltip = useTemplateRef<TippyComponent | null>(
  "templatesTooltip"
);
const popperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["top-start"] },
    },
  ],
});

const setDevice = (selectedDevice: MediaDeviceInfo) => {
  selectedInputDevice.value = selectedDevice;
  emit("device:select", selectedDevice.deviceId, type);
  hideDropdown();
};

const hideDropdown = () => {
  templatesTooltip.value?.hide();
};
</script>
